import type { ScanGroupOptimized, ScanPartOptimized, ScanRootOptimized, ScanVideo } from '@kaiizen/kai-scan-template-system';

import { getPartLocalVideoEntries, getPartVideoEntries } from '@/Client/Lib/scopeNavigatorPartPresentersLib';

/** Which focus-panel route opened the toolbox — drives videoOffset lookup, not strip tile dedupe. */
export type VideoToolboxEditorPlaybackContext =
  | { editorScope: 'scope' }
  | { editorScope: 'group'; groupId: string }
  | { editorScope: 'task'; groupId: string; taskId: string };

const findPartForEditorContext = (
  scanRoot: ScanRootOptimized,
  groups: readonly ScanGroupOptimized[],
  editorContext: VideoToolboxEditorPlaybackContext,
): ScanPartOptimized | undefined => {
  if (editorContext.editorScope === 'task') {
    const group = groups.find(candidate => candidate.id === editorContext.groupId);
    return group?.tasks?.find(task => task.id === editorContext.taskId);
  }

  if (editorContext.editorScope === 'group') {
    return groups.find(group => group.id === editorContext.groupId);
  }

  return undefined;
};

const findScanVideoOnPart = (
  part: ScanPartOptimized | undefined,
  scanVideoId: string,
  localVideosOnly: boolean,
): ScanVideo | undefined => {
  const entries = localVideosOnly ? getPartLocalVideoEntries(part) : getPartVideoEntries(part);
  return entries.find(video => video.videoId === scanVideoId);
};

const isRootWalkthroughVideoId = (
  scanVideoId: string,
  scanRoot: ScanRootOptimized,
): boolean => getPartLocalVideoEntries(scanRoot).some(video => video.videoId === scanVideoId);

/**
 * Where to start playback when opening the toolbox from a focus-panel route.
 * All-scope `/edit` always starts at 0; group/task routes use that part's `videoOffset`.
 * Clipped assets play from 0 because their offset is source provenance only.
 */
export const resolveVideoToolboxPlaybackStartSeconds = (
  scanVideoId: string,
  scanRoot: ScanRootOptimized,
  groups: readonly ScanGroupOptimized[],
  editorContext: VideoToolboxEditorPlaybackContext,
): number | undefined => {
  if (editorContext.editorScope === 'scope') return 0;

  const part = findPartForEditorContext(scanRoot, groups, editorContext);
  const scanVideo = findScanVideoOnPart(part, scanVideoId, true);
  const offset = scanVideo?.videoOffset;
  if (!offset) return undefined;

  if (isRootWalkthroughVideoId(scanVideoId, scanRoot)) {
    return offset.startSeconds;
  }

  return 0;
};
